// ---------------------------------------------------------------------------
// Carts set aside.
//
// Nothing here touches the product or inventory caches, and that is not an
// oversight: holding a cart moves no stock, so a screen showing stock has
// nothing to re-read. The moment this file starts invalidating inventory,
// somebody has made holding reserve something.
// ---------------------------------------------------------------------------

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import type { HeldOrder, RestoreResult } from '../types';

const HELD_KEY = ['held-orders'] as const;

export function useHeldOrders(
  options: { scope?: 'own' | 'all'; includeFinished?: boolean } = {},
  enabled = true,
) {
  const scope = options.scope ?? 'own';

  return useQuery({
    queryKey: [...HELD_KEY, scope, options.includeFinished ?? false],
    queryFn: async () => {
      const params = new URLSearchParams({ scope });
      if (options.includeFinished) params.set('includeFinished', 'true');

      const response = await api.get<{ heldOrders: HeldOrder[]; scope: string }>(
        `/held-orders?${params.toString()}`,
      );
      return response.heldOrders;
    },
    enabled,
    // A cashier glances at the spike between customers, so this stays fresh
    // enough to be useful without polling.
    staleTime: 10_000,
  });
}

export function useHoldCart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      id?: string;
      customerId?: string | null;
      label?: string | null;
      customerName?: string | null;
      phone?: string | null;
      orderNote?: string | null;
      items: Array<{
        productId: string;
        quantityMode: string;
        quantity: string;
        customAmount?: string;
        note?: string | null;
      }>;
    }) => api.post<{ heldOrder: HeldOrder; message: string }>('/held-orders', input),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: HELD_KEY });
    },
  });
}

/// Pull a cart back onto the till.
///
/// The `version` is what stops two cashiers taking the same cart. It comes
/// from the list the screen is showing; a stale one is refused with a 409 and
/// the cashier is told to refresh.
export function useRestoreHeldOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      api.post<RestoreResult>(`/held-orders/${id}/restore`, { version }),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: HELD_KEY });
    },
  });
}

export function useCancelHeldOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<{ heldOrder: HeldOrder; message: string }>(`/held-orders/${id}/cancel`, { reason }),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: HELD_KEY });
    },
  });
}
